import { useLayoutEffect, useRef, useState } from 'react';

/** What a chart draws at before it has been measured, and in a test without layout. */
export const DEFAULT_CHART_WIDTH = 960;

/** Below this many pixels a chart is on a phone: fewer labels, a readout strip, a turned heatmap. */
export const NARROW_CHART = 480;

/**
 * The real pixel width of a chart's frame, so the SVG is drawn at 1:1 and its 11px text stays 11px
 * at any width (a fixed viewBox shrunk onto a phone made the text 4px). Falls back to
 * {@link DEFAULT_CHART_WIDTH} where there is no ResizeObserver.
 */
export function useMeasuredWidth<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(DEFAULT_CHART_WIDTH);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => {
      const w = Math.round(el.getBoundingClientRect().width);
      if (w > 0) setWidth(w);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { ref, width, narrow: width < NARROW_CHART };
}
